// store/apis/properties.api.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";
import { clientsApi } from "./clients.api";
import { listingChecklistApi } from "./listingChecklist.api";
import { dashboardApi } from "./dashboard.api";

export const propertiesApi = createApi({
  reducerPath: "propertiesApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Property", "PropertyStats", "SavedProperties"],
  endpoints: (build) => ({
    // Get all properties with filters and pagination
    getAllProperties: build.query({
      query: ({
        agentId,
        status,
        type,
        page = 1,
        limit = 10,
        search = "",
        minPrice,
        maxPrice,
        minBeds,
        maxBeds,
        sortBy = "createdAt",
        sortOrder = "desc",
      } = {}) => {
        const params = new URLSearchParams();

        if (agentId) params.append("agentId", agentId);
        if (status) params.append("status", status);
        if (type) params.append("type", type);
        if (page) params.append("page", page.toString());
        if (limit) params.append("limit", limit.toString());
        if (search) params.append("search", search);
        if (minPrice !== undefined) params.append("minPrice", String(minPrice));
        if (maxPrice !== undefined) params.append("maxPrice", String(maxPrice));
        if (minBeds !== undefined) params.append("minBeds", String(minBeds));
        if (maxBeds !== undefined) params.append("maxBeds", String(maxBeds));
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return {
          url: `/properties?${params.toString()}`,
          method: "GET",
        };
      },
      // FIXED: Safe providesTags that handles undefined/null properties
      providesTags: (result) => {
        if (result?.properties && Array.isArray(result.properties)) {
          return [
            ...result.properties.map(({ _id }) => ({ type: "Property", id: _id })),
            { type: "Property", id: "LIST" },
          ];
        }
        return [{ type: "Property", id: "LIST" }];
      },
    }),

    // Get property by ID
    getPropertyById: build.query({
      query: (id) => ({
        url: `/properties/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Property", id }],
    }),

    // Toggle saved status
    toggleSavedProperty: build.mutation({
      query: (id) => ({
        url: `/properties/${id}/toggle-saved`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Property", id },
        { type: "Property", id: "LIST" },
        "SavedProperties",
        "PropertyStats",
      ],
    }),

    // Update property (PUT /properties/:id)
    updateProperty: build.mutation({
      query: ({ id, body }) => ({
        url: `/properties/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Property", id },
        { type: "Property", id: "LIST" },
        "PropertyStats",
      ],
    }),

    // Delete property (DELETE /properties/:id)
    deleteProperty: build.mutation({
      query: (id) => ({
        url: `/properties/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Property", id },
        { type: "Property", id: "LIST" },
        "PropertyStats",
      ],
      // Cross-API cascade: the backend also pulls this address out of
      // Client.properties[] subdocs, clears Client.propertyAddress
      // where it matched, and deletes any ListingChecklist tracking
      // the same address. Invalidate those caches so the Clients /
      // Transactions / Listing-Checklist views refetch and the user
      // doesn't see the deleted listing lingering anywhere.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(clientsApi.util.invalidateTags(["Client", "ClientStats"]));
          dispatch(listingChecklistApi.util.invalidateTags(["ListingChecklists", "ChecklistStats"]));
        } catch { /* user already saw the toast */ }
      },
    }),

    // Get property statistics
    getPropertyStats: build.query({
      query: () => ({
        url: "/properties/stats/overview",
        method: "GET",
      }),
      providesTags: ["PropertyStats"],
    }),

    // Get saved properties
    getSavedProperties: build.query({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: "/properties/saved",
        method: "GET",
        params: { page, limit },
      }),
      // FIXED: Safe providesTags that handles undefined/null properties
      providesTags: (result) => {
        if (result?.properties && Array.isArray(result.properties)) {
          return [
            ...result.properties.map(({ _id }) => ({ type: "Property", id: _id })),
            "SavedProperties",
          ];
        }
        return ["SavedProperties"];
      },
    }),

    // Get properties by Agent ID
    getPropertiesByAgentId: build.query({
      query: ({
        agentId,
        status,
        type,
        page = 1,
        limit = 10,
        search = "",
        minPrice,
        maxPrice,
        minBeds,
        maxBeds,
        sortBy = "createdAt",
        sortOrder = "desc",
      } = {}) => {
        const params = new URLSearchParams();

        if (status) params.append("status", status);
        if (type) params.append("type", type);
        if (page) params.append("page", String(page));
        if (limit) params.append("limit", String(limit));
        if (search) params.append("search", search);
        if (minPrice !== undefined) params.append("minPrice", String(minPrice));
        if (maxPrice !== undefined) params.append("maxPrice", String(maxPrice));
        if (minBeds !== undefined) params.append("minBeds", String(minBeds));
        if (maxBeds !== undefined) params.append("maxBeds", String(maxBeds));
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return {
          url: `/properties/agent/${agentId}?${params.toString()}`,
          method: "GET",
        };
      },
      // FIXED: Safe providesTags that handles undefined/null properties
      providesTags: (result) => {
        if (result?.properties && Array.isArray(result.properties)) {
          return [
            ...result.properties.map(({ _id }) => ({ type: "Property", id: _id })),
            { type: "Property", id: "AGENT_LIST" },
          ];
        }
        return [{ type: "Property", id: "AGENT_LIST" }];
      },
    }),

    // Create property (POST /properties)
    createProperty: build.mutation({
      query: (body) => ({
        url: "/properties",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Property", id: "LIST" },
        { type: "Property", id: "AGENT_LIST" },
        "PropertyStats",
      ],
      // Cross-API: dashboard "Active Listings" tile reads
      // DashboardStats which lives in dashboardApi. Refetch so the
      // dashboard quick-add reflects the new property immediately.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(dashboardApi.util.invalidateTags(["DashboardStats"]));
        } catch { /* mutation already toasted */ }
      },
    }),

    // Upload properties from CSV (POST /properties/upload-csv)
    uploadPropertiesFromCSV: build.mutation({
      query: (formData) => ({
        url: "/properties/upload-csv",
        method: "POST",
        body: formData,
        formData: true,
      }),
      invalidatesTags: [
        { type: "Property", id: "LIST" },
        { type: "Property", id: "AGENT_LIST" },
        "PropertyStats",
      ],
    }),

    // Save property from RentCast (copy to user's CRM)
    saveFromRentCast: build.mutation({
      query: (propertyData) => ({
        url: "/properties/save-from-rentcast",
        method: "POST",
        body: propertyData,
      }),
      invalidatesTags: [
        { type: "Property", id: "LIST" },
        { type: "Property", id: "AGENT_LIST" },
        "PropertyStats",
      ],
    }),

    // Get my listings (agent's own properties)
    getMyListings: build.query({
      query: ({ stage, status } = {}) => {
        const params = new URLSearchParams();
        if (stage) params.append("stage", stage);
        if (status) params.append("status", status);
        return {
          url: `/properties/my-listings?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result) => {
        if (result?.data && Array.isArray(result.data)) {
          return [
            ...result.data.map(({ _id }) => ({ type: "Property", id: _id })),
            { type: "Property", id: "MY_LISTINGS" },
          ];
        }
        return [{ type: "Property", id: "MY_LISTINGS" }];
      },
    }),

    // Upload property images
    uploadPropertyImages: build.mutation({
      query: ({ id, formData }) => ({
        url: `/properties/${id}/images`,
        method: "POST",
        body: formData,
        formData: true,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Property", id },
        { type: "Property", id: "LIST" },
        { type: "Property", id: "MY_LISTINGS" },
      ],
    }),

    // Delete property image
    deletePropertyImage: build.mutation({
      query: ({ id, imageIndex }) => ({
        url: `/properties/${id}/images/${imageIndex}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Property", id },
        { type: "Property", id: "MY_LISTINGS" },
      ],
    }),
  }),
});

export const {
  useGetAllPropertiesQuery,
  useGetPropertyByIdQuery,
  useToggleSavedPropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useGetPropertyStatsQuery,
  useGetSavedPropertiesQuery,
  useGetPropertiesByAgentIdQuery,
  useCreatePropertyMutation,
  useUploadPropertiesFromCSVMutation,
  useSaveFromRentCastMutation,
  useGetMyListingsQuery,
  useUploadPropertyImagesMutation,
  useDeletePropertyImageMutation,
} = propertiesApi;
